import React, { useState } from 'react';
import { setErrorModal } from '@app/slices/modals';

import {
  Card,
  Title,
  Button,
  Input,
  CardHeader,
  CardContent,
  CardFooter,
  InputContainer,
  Icon,
} from '@common/components';

import { useAppDispatch } from '@app/hooks/index';
import { isEmail, isEmpty } from '@app/util/validations';

import { faEnvelope, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { useLoginMutation } from '@auth/api/auth';
import { setCredentials } from '@auth/slices/auth';
import { useNavigate } from 'react-router-dom';
import { useTogglePasswordVisibility } from '@auth/hooks/useTooglePasswordVisibility';
import { useTranslation } from 'react-i18next';
import { useInputErrors } from '@app/hooks/useInputErrors';

import loginClasess from './LoginView.module.scss';

export default function LoginView() {
  const { t } = useTranslation('auth\\LoginView');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [login, { isLoading }] = useLoginMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { errors, resetErrors, setErrors, updateErrors } = useInputErrors();

  const onValidate = () => {
    let isValid = resetErrors();
    setErrors({});

    if (isEmpty(email)) {
      isValid = updateErrors({
        email: t('inputEmailValidationRequired'),
      });
    } else if (!isEmail(email)) {
      isValid = updateErrors({
        email: t('inputEmailValidationFormat'),
      });
    }
    if (isEmpty(password)) {
      isValid = updateErrors({
        password: t('inputPasswordValidationRequired'),
      });
    }

    return isValid;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (onValidate() && email && password) {
      try {
        const { data } = await login({ email, password }).unwrap();
        if (data) {
          dispatch(setCredentials(data));
          navigate('/dashboard');
        }
      } catch (error) {
        dispatch(
          setErrorModal({
            ...error,
            ...{ data: { message: t('errorBadCredentials') } },
          }),
        );
      }
    }
  };
  const { passwordVisibility, passwordIcon, handlePasswordVisibility } =
    useTogglePasswordVisibility();

  return (
    <section className={loginClasess.login}>
      <form onSubmit={onSubmit}>
        <Card className={loginClasess.card}>
          <CardHeader>
            <Title>
              {t('headerTitle')}{' '}
              <img alt="datalab-logo" src="/img/datalab-logo.svg" width={140} />
            </Title>
          </CardHeader>
          <CardContent>
            <InputContainer
              suffix={
                <Icon
                  icon={faEnvelope}
                  className={errors.email && loginClasess.inputError}
                  color="primary"
                />
              }
              placeholder={t('inputEmailLabel')}
              name="email"
              error={errors.email}
            >
              <Input
                name="email"
                fullWidth
                type="email"
                placeholder={t('inputEmailPlaceholder')}
                onChange={setEmail}
                value={email}
              />
            </InputContainer>
            <InputContainer
              suffix={
                <Button
                  minimal
                  onClick={handlePasswordVisibility}
                  color="primary"
                >
                  <Icon
                    icon={passwordIcon}
                    className={errors.password && loginClasess.inputError}
                  />
                </Button>
              }
              placeholder={t('inputPasswordLabel')}
              name="password"
              error={errors.password}
            >
              <Input
                name="password"
                fullWidth
                type={passwordVisibility ? 'text' : 'password'}
                placeholder={t('inputPasswordPlaceholder')}
                onChange={setPassword}
                value={password}
              />
            </InputContainer>
          </CardContent>
          <CardFooter>
            <Button
              fullWidth
              type="submit"
              variant="flat"
              color="accent"
              disabled={isLoading}
              prefix={<Icon icon={faPaperPlane} />}
            >
              {t('submitButtonLabel')}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </section>
  );
}
